import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  DemographicsContainer,
  DemographicsTitleDiv,
  DemographicsContentDiv,
  SourceDiv,
  DemographicsItem,
  ItemTitle,
  ItemValue,
  ItemValueDiv,
  ItemsContainer,
  DemographicsIconDiv,
  IconButton,
  StyledSearchIcon,
  StyledCloseIcon,
  StyledReloadIcon,
  StyledConfirmationIcon,
  ItemTitleDiv,
  EditTextButton,
  ConfirmationIconWrapper
} from './styledComponents';

import CustomInput from '../../CustomComponents/CustomInput';
import InferenceEditor from './InferenceEditor';
import { Button, Empty, Select, Spin, Tooltip, notification } from 'antd';
import { StyledAsterisk } from './InferenceEditor/styledComponents';
import { getCptCodes, getCptReferences, updateCptCodes } from '../../../Apis/Biller';

const Demographics = ({
  sections,
  theme,
  content,
  inference,
  loading,
  fetchContents,
  updateInferenceData,
  updateInferenceFlag,
  jobId,
  setInferenceAnnotation,
  setMatchedCodes,
  matchedCodes,
  inferenceAnnotation,
  editorData,
  activeSection,
  emptySection,
  updateInferenceLoading,
  setUpdateInferenceLoading,
  setHighlightWord,
  highlightWord,
  selectedCodesRef
}) => {
  const [searchClicked, setSearchClicked] = useState(false);
  const [previewData, setPreviewData] = useState('');
  const [demographicsContents, setDemographicsContents] = useState([]);
  const [editKey, setEditKey] = useState(null);
  // const [matchedCodes, setMatchedCodes] = useState([]);
  const editorRef = useRef(null);
  const [cptCodes, setCptCodes] = useState([]);
  const [cptReferences, setCptReferences] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [showAddSelect, setShowAddSelect] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [cptCodesLoaded, setCptCodesLoaded] = useState(false);
  const [refsLoaded, setRefsLoaded] = useState(false);
  const initialized = useRef(false);

  const highlightCodeInEditor = code => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    const text = editor.getText();
    editor.formatText(0, text.length, { background: 'transparent' });
    let index = text.indexOf(code);
    while (index !== -1) {
      editor.formatText(index, code.length, { background: 'yellow' });
      index = text.indexOf(code, index + code.length);
    }
  };
  const clearHighlight = () => {
    const editor = editorRef.current?.getEditor();
    if (editor) {
      const text = editor.getText();
      editor.formatText(0, text.length, { background: 'transparent' });
    }
  };
  useEffect(() => {
    getCptCodes(jobId)
      .then(response => {
        if (response.data.success) {
          const codes = response.data.result.map(item => ({
            cptCode: item.cptCode,
            description: item.description,
            category: item.category,
            content: item.content
          }));
          setCptCodes(codes);
        } else {
          notification['error']({
            message: 'CPT Codes Fetch Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => console.log('Error fetching CPT codes:', err))
      .finally(() => setCptCodesLoaded(true));
  }, []);

  useEffect(() => {
    getCptReferences()
      .then(response => {
        if (response.data.success) setCptReferences(response.data.result);
      })
      .catch(err => console.log('Error fetching CPT references:', err))
      .finally(() => setRefsLoaded(true));
  }, []);

  useEffect(() => {
    if (!initialized.current && cptCodesLoaded && refsLoaded) {
      initialized.current = true;
      setSelectedCodes(
        cptCodes
          .map(c => cptReferences.find(r => r.cptCode === c.cptCode))
          .filter(Boolean)
      );
    }
  }, [cptCodesLoaded, refsLoaded]); // eslint-disable-line

  useEffect(() => {
    if (selectedCodesRef) selectedCodesRef.current = selectedCodes;
  }, [selectedCodes, selectedCodesRef]);

  const handleDeleteCode = useCallback(id => {
    setSelectedCodes(prev => prev.filter(c => c.id !== id));
  }, []);

  const handleAddCode = value => {
    const ref = cptReferences.find(r => r.id === value);
    if (ref) {
      setSelectedCodes(prev => [...prev, ref]);
      setShowAddSelect(false);
    }
  };

  const handleUpdateCptCodes = () => {
    const sessionId = JSON.parse(localStorage.getItem('user'))?.sessionId;
    setUpdateLoading(true);
    updateCptCodes({ cptCodeIds: selectedCodes.map(c => c.id), jobId }, sessionId)
      .then(() => notification['success']({ message: 'CPT Codes updated successfully' }))
      .catch(() => notification['error']({ message: 'Failed to update CPT Codes' }))
      .finally(() => setUpdateLoading(false));
  };

  useEffect(() => {}, [cptCodes, matchedCodes]);
  useEffect(() => {
    if (sections && sections.length > 0) {
      const stripHtml = html => html.replace(/<[^>]*>/g, ' ');

      const content = sections
        .map(sec => {
          if (Array.isArray(sec.content)) {
            return sec.content
              .map(c => stripHtml(c.value || '').toLowerCase())
              .join(' ');
          } else {
            return stripHtml(sec.content || '').toLowerCase();
          }
        })
        .join(' ');

      // const foundCodes = CPT_CODES.filter(code =>
      //   content.includes(code.toLowerCase())
      // );
      const foundCodes = cptCodes.filter(item =>
        content.toLowerCase().includes(item.cptCode.toLowerCase())
      );

      setMatchedCodes(foundCodes);
    }
  }, [sections, cptCodes]);

  useEffect(() => {
    setDemographicsContents(content);
    setEditKey(false);
  }, [content]);

  useEffect(() => {
    editorData.forEach(item => {
      if (activeSection === item.id) {
        setPreviewData(item.content && item.content[0].value);
      }
    });
  });

  const searchValues = val => {
    let filteredContents;
    if (inference) {
      filteredContents = content.filter(
        item => item.key && item.key.toLowerCase().includes(val.toLowerCase())
      );
    } else {
      filteredContents = content.filter(
        item =>
          (item.key && item.key.toLowerCase().includes(val.toLowerCase())) ||
          (item.value && item.value.toLowerCase().includes(val.toLowerCase()))
      );
    }

    setDemographicsContents(filteredContents);
    if (val.length === 0) {
      setDemographicsContents(content);
    }
  };

  useEffect(() => {
    if (searchClicked === false) {
      setDemographicsContents(content);
    }
    // eslint-disable-next-line
  }, [searchClicked]);

  const editFlagHandler = (key, isEdited) => {
    if (isEdited) {
      let inferenceObject = {
        key: key,
        edited: false
      };
      updateInferenceFlag(inferenceObject);
    } else {
      setEditKey(key);
    }
  };

  const renderDemographicsItem = item => {
    const annotationAvailable =
      item.annotations &&
      item.annotations.length > 0 &&
      item.annotations[0].startIndex !== item.annotations[0].endIndex;
    const isEmpty = !inference && item.value === null;
    return item.type !== 'Dynamic Table' && item.key !== editKey ? (
      !isEmpty && (
        <DemographicsItem
          selected={inferenceAnnotation && inferenceAnnotation.key === item.key}
          headingTextColor={theme['@primary-color']}
          onClick={() => {
            inference && annotationAvailable && setInferenceAnnotation(item);
          }}
          inference={inference}
          annotationAvailable={annotationAvailable}
        >
          <ItemTitleDiv>
            <ItemTitle textColor={theme['@primary-color']}>
              {inference && item.edited && (
                <Tooltip title='Updated Inference'>
                  <ConfirmationIconWrapper>
                    <StyledConfirmationIcon height={'16'} width={'16'} />
                  </ConfirmationIconWrapper>
                </Tooltip>
              )}
              {item.key}
              {item.mandatory && <StyledAsterisk>*</StyledAsterisk>}
            </ItemTitle>
            {inference && (
              <EditTextButton
                type='text'
                textColor={theme['@text-color']}
                onClick={e => {
                  e.stopPropagation();
                  editFlagHandler(item.key, item.edited);
                }}
              >
                {item.edited ? 'Allow Edit' : 'Edit'}
              </EditTextButton>
            )}
          </ItemTitleDiv>
          <ItemValueDiv>
            <ItemValue textColor={theme['@text-color']}>
              {item.value && item.value.toString()}
            </ItemValue>
          </ItemValueDiv>
        </DemographicsItem>
      )
    ) : (
      <InferenceEditor
        theme={theme}
        inferences={[item]}
        updateInferenceData={updateInferenceData}
        updateInferenceFlag={updateInferenceFlag}
        setEditKey={setEditKey}
        previewData={previewData}
        updateInferenceLoading={updateInferenceLoading}
        setUpdateInferenceLoading={setUpdateInferenceLoading}
        activeSection={activeSection}
        fetchContents={fetchContents}
      />
    );
  };
  return (
    <DemographicsContainer>
      <DemographicsTitleDiv background={theme && theme['@text-color']}>
        Billing
      </DemographicsTitleDiv>
      <DemographicsContentDiv>
        <SourceDiv>Code found in text</SourceDiv>

        <ItemsContainer style={{ minHeight: 'calc(100vh - 399px)', maxHeight: 'calc(100vh - 399px)' }}>
          {selectedCodes.map((code, index) => (
            <div
              key={index}
              className='cpt-code-item'
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseEnter={() => setHighlightWord(code.cptCode)}
              onMouseLeave={clearHighlight}
            >
              <span>{code.cptCode}</span>
              <Button
                type='text'
                size='small'
                style={{ color: '#ff4d4f', fontWeight: 700, padding: '0 4px' }}
                onClick={e => { e.stopPropagation(); handleDeleteCode(code.id); }}
              >
                ×
              </Button>
            </div>
          ))}
          {showAddSelect ? (
            <Select
              showSearch
              autoFocus
              defaultOpen
              virtual={false}
              placeholder='Search CPT code'
              style={{ width: '100%', marginTop: '4px' }}
              optionFilterProp='label'
              onChange={handleAddCode}
              onBlur={() => setShowAddSelect(false)}
              options={cptReferences
                .filter(r => !selectedCodes.some(s => s.id === r.id))
                .map(r => ({ value: r.id, label: `${r.cptCode} - ${r.description}` }))}
            />
          ) : (
            <Button
              type='dashed'
              style={{ width: '100%', marginTop: '4px' }}
              onClick={() => setShowAddSelect(true)}
            >
              + Add CPT Code
            </Button>
          )}
        </ItemsContainer>

        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
          <Button
            type='primary'
            loading={updateLoading}
            style={{ height: 40, width: '100%', fontWeight: 700, fontSize: 16 }}
            onClick={handleUpdateCptCodes}
          >
            Update CPT Code
          </Button>
        </div>

        <style>
          {`
    .cpt-code-item {
      margin-bottom: 10px;         
      margin-left: 8px;           
      font-size: 16px;              
      font-weight: 500;            
      padding: 6px 10px;           
      border: 1px solid transparent;
      border-radius: 4px;           
      transition: all 0.2s ease-in-out;
      cursor: pointer;
    }

    .cpt-code-item:hover {
      border: 1px solid #1890ff;   
      background: #f0f8ff;          
    }
  `}
        </style>
      </DemographicsContentDiv>
    </DemographicsContainer>
  );
};

export default Demographics;
